import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type IdleAgent } from '../transport/api'
import { onEdgeEvent } from '../servers/commandStream'
import { relayTarget } from '../transport/relay'
import {
  getIdleEverywhere,
  idleKey,
  mergeIdleMachine,
  type IdleEverywhere,
  type IdleMachine,
} from './everywhere'




/**
 * What this hook has actually seen, for a harness to read.
 *
 * `frames` — pty bursts delivered to this hook at all; `usable` — those carrying
 * output or an act ping; `asks` — refetches it started; `partials` — per-machine
 * answers published. Four numbers that separate "nothing reached us", "frames reached
 * us but none counted", "we asked and the answer was slow" and "the answer came back
 * without this agent in it" — four causes that are one dark dot on screen.
 *
 * They are here because the 2026-08-29 investigation could not tell those apart and
 * spent several deploys guessing. Same reason liveBlock carries `window.__livePane`;
 * the cost is four integers.
 */
const idleWhy = { frames: 0, usable: 0, asks: 0, partials: 0, stale: 0, pokes: 0, switches: 0 }

/**
 * THE SWITCH STORE — owner, 2026-08-30, 設計結案: Agent 的狀態完全由 edge 控制,
 * 並且發送信號給前端做開關. 完全不需要前端主動探索.
 *
 * Each machine's edge pushes its WHOLE busy set (`idle_status {busy:[...]}`,
 * statusWatcher.ts) on every change, once at boot, and on a slow re-affirm beat
 * while non-empty. This map is those switches, verbatim; presenceFor reads it
 * FIRST and only falls back to the fetched row's status for a machine whose
 * edge predates the switches. A machine that is offline has no rows at all —
 * the owner's own closing of the loop: 沒有上線就顯示沒有上線.
 */
const busySwitch = new Map<string, Set<string>>()
/** Same frame, second fact: which sessions are blocked on a question. */
const askingSwitch = new Map<string, Set<string>>()
const switchListeners = new Set<() => void>()

const sameSet = (a: ReadonlySet<string> | undefined, b: ReadonlySet<string>): boolean =>
  !!a && a.size === b.size && [...b].every((x) => a.has(x))

/**
 * One frame into the switches. True when it CHANGED them — only then is anything repainted.
 *
 * Owner, 2026-11-04: 功能變多了之後, 現在 frontend app / UI 有時候會變得很卡. Each component using this hook (the app, the
 * rail, a room, the activity feed …) used to hold its own stream listener, and each listener repainted every one of
 * them, on every frame — including the re-affirm an edge sends every 30s while anything is busy, which says nothing new.
 * A repaint of the app is a render of everything on screen.
 */
export function applySwitchFrame(data: Record<string, unknown>): boolean {
  const device = typeof data['device_id'] === 'string' ? (data['device_id'] as string) : null
  if (!device) return false
  idleWhy.switches++
  const busy = new Set((Array.isArray(data['busy']) ? (data['busy'] as unknown[]) : []).filter((x): x is string => typeof x === 'string'))
  let changed = false
  if (!sameSet(busySwitch.get(device), busy)) {
    busySwitch.set(device, busy)
    changed = true
  }
  // `asking` arrived later than `busy` (2026-08-30); an older edge's frame
  // simply carries none, and the row fallback in needsInputFor still answers.
  if (Array.isArray(data['asking'])) {
    const asking = new Set((data['asking'] as unknown[]).filter((x): x is string => typeof x === 'string'))
    if (!sameSet(askingSwitch.get(device), asking)) {
      askingSwitch.set(device, asking)
      changed = true
    }
  }
  if (changed) switchRev++
  return changed
}

/** Moves whenever a switch does — for a memo whose rows read the switches through presenceFor (the rail's rows). */
let switchRev = 0
export function switchRevision(): number {
  return switchRev
}

/**
 * Each mounted hook listens; the first to see a frame applies it, and repaints every hook ONCE — only when it changed
 * something. The others find nothing new and do nothing (each used to repaint them all: K hooks, K² repaints a frame).
 */
function listenForSwitches(): () => void {
  return onEdgeEvent((event, data) => {
    if (event !== 'idle_status') return
    if (!applySwitchFrame(data)) return
    for (const fn of [...switchListeners]) fn()
  })
}

/**
 * Which sweep is the current one.
 *
 * THREE ASKS PER BURST, and a sweep over ten machines takes seconds — so sweeps
 * OVERLAP by construction. Each publishes its machines as they land, straight into
 * the cache, which is exactly the ordering react-query used to guarantee for us when
 * a sweep was one atomic result: it applies the last fetch to RESOLVE, and drops the
 * rest. Publishing partials opts out of that guarantee, so the ordering has to be
 * restored here or an older sweep's `idle` lands after a newer sweep's `active` and
 * switches the dot off under a working agent.
 *
 * Owner, 2026-08-29: 非常不穩定的狀態 […] 要隔很久才會出現. A stale write is exactly
 * that shape — right sometimes, wrong sometimes, worse the more machines are slow.
 *
 * The rule is newest DATA PER MACHINE, not newest sweep. "Only the newest sweep may
 * write" was the first version and it is wrong in the other direction: asks come
 * 1.5s apart during a burst while a sweep takes seconds, so every sweep would be
 * superseded before finishing and only the LAST one — the settle, after the turn is
 * over — would ever apply. That is the reported symptom, manufactured. So each
 * machine remembers which sweep last spoke for it, and an older sweep is refused
 * only where a newer one has already answered.
 */
let sweepGen = 0
const spokeFor = new Map<string, number>()
if (typeof window !== 'undefined') {
  ;(window as unknown as { __idleWhy: typeof idleWhy }).__idleWhy = idleWhy
}

/**
 * How long a status may be carried for a machine that did not answer.
 *
 * Long enough to ride out a slow relay call or a missed roster fetch — the
 * transient that made the busy halo blink — and short enough that a machine
 * which is genuinely gone stops being described as working. It is a bound on a
 * STALE CLAIM, not a poll interval: nothing fires at this cadence, and the value
 * is only ever compared against when the next answer happens to arrive.
 */
const HOLD_MS = 20_000

/**
 * What every agent on every paired machine is doing. One query feeds every consumer
 * (rail dots, kind badges, previews, unread badges, the project busy float), so
 * however many components subscribe there is exactly one ask.
 *
 * NOT A POLL, and there is no interval here on purpose — the owner's standing rule is
 * that nothing may be built on a heartbeat, and `servers/noHeartbeat.test.ts` enforces
 * it. This refreshes when the user acts, when the window is focused, and when the
 * stream says the machines changed.
 *
 * The comment this replaces described a 5-second poll in detail. The poll had already
 * been removed; only its explanation survived, which is worse than either — a reader
 * checking why the rail felt stale would have found a mechanism that was not there.
 */
export function useIdleStatus() {
  const qc = useQueryClient()
  /**
   * One cache writer for both the sweep and the push-driven scoped ask, so the
   * per-machine ordering rule (newest DATA PER MACHINE — see spokeFor) cannot
   * fork into two copies that disagree.
   */
  const writerFor = (mine: number) => (m: IdleMachine) => {
    // Refused only where a NEWER sweep has already answered for this machine.
    // Not "only the newest sweep may write" — see the note on spokeFor.
    if (mine < (spokeFor.get(m.device_id) ?? 0)) {
      idleWhy.stale++
      return
    }
    spokeFor.set(m.device_id, mine)
    idleWhy.partials++
    qc.setQueryData<IdleEverywhere>(['idle-status'], (prev) => mergeIdleMachine(prev, m))
  }
  const q = useQuery({
    queryKey: ['idle-status'],
    /*
     * EACH MACHINE'S ANSWER REACHES THE SCREEN WHEN IT LANDS.
     *
     * Measured against the owner's report (2026-08-29: Agent 忙碌狀態的偵測感覺非常遲緩
     * […] 等過十秒), with the pane, the edge's own answer and the rendered dot on one
     * timeline: the edge said `active` at 3.7s and the dot lit at 13.8s; the edge said
     * `idle` at 23.7s and the dot went out at 36.9s. Ten seconds late in BOTH
     * directions, and the edge was never the slow half.
     *
     * The cause was that the sweep resolved as one lump: the account has ten paired
     * machines, an unreachable one costs a 4s relay deadline plus a retry, and the
     * machine actually on screen — which answers in about 40ms — waited for all of
     * them. `onMachine` publishes each share as it arrives, MERGED so the machines
     * that have not answered yet keep the rows they had. Without that merge a partial
     * answer would read as "every other machine is gone" and darken their dots, which
     * is the same trap `held` below exists for.
     *
     * `first` puts the machine being watched at the front of the queue, so its answer
     * is in the first wave rather than wherever the device list happened to put it.
     */
    queryFn: () => {
      const mine = ++sweepGen
      return getIdleEverywhere({ first: relayTarget(), onMachine: writerFor(mine) })
    },
    /*
     * This asks N machines in parallel, so a re-ask is N requests rather than one.
     * That is a reason to ask on real signals only — which is what the no-heartbeat
     * rule already required — and not a reason to serve a stale answer: react-query
     * would otherwise dedupe a deliberate refetch away as "fresh enough".
     */
    staleTime: 0,
  })


  /**
   * Refetch the moment the app comes back — imperatively.
   *
   * Two things had to be measured to get this right. `refetchOnWindowFocus` is on
   * globally and fired nothing: zero requests in the 700ms after a
   * visibilitychange, against two in eight seconds of steady polling. Nor did
   * `invalidateQueries` — and instrumenting the page showed why it looked dead
   * when it was not: the event DID reach a listener and visibilityState WAS
   * 'visible', so the handler ran. Invalidation marks a query stale and leaves the
   * fetch to the focus manager, which in a headless page (and possibly a WebView)
   * does not believe it has focus.
   *
   * `refetch()` asks directly and does not consult that state. It is also the
   * honest expression of the intent: not "this might be stale" but "get it now".
   *
   * This is what carries freshness, not the interval — at 5s versus 2.5s the
   * difference while watching is small, and on the first frame after opening the
   * app it is everything (avengents-app-builder).
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void q.refetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Ask again once a terminal has gone quiet.
   *
   *   owner, 2026-08-07: 你上一個 session 的活動已經結束了. 但是卻還是顯示 Busy.
   *
   * THE DETECTION WAS NOT WRONG. Asked at that moment the edge said `idle` — its rule
   * reads the tmux pane, and a settled line ("Crunched for 8m 6s") carries none of the
   * mid-turn tells it looks for. Nobody asked it. I had removed this hook's 5s poll
   * under the no-heartbeat rule and put nothing in its place, leaving four triggers —
   * a devices frame, a kill, a mark-read, a create — not one of which means "an agent
   * stopped working". Watch the screen without touching it and the last answer stands
   * forever, which is exactly what a stuck Busy is.
   *
   * The rule says to subscribe to the event rather than sample it, and the event was
   * already on the wire: a working pane repaints, a finished one stops. So this is
   * edge-triggered on the SILENCE after output — one ask per settle, never per frame,
   * and nothing at all while the terminal is quiet. That last part is what keeps it
   * from being the heartbeat it replaces; `idleSettle.test.tsx` fails if it becomes one.
   *
   * WHAT IT DOES NOT COVER: an agent on a machine whose terminal you are not attached
   * to. No pty frames arrive for it, so its dot still waits for one of the four
   * triggers. Closing that needs the edge to push a status change — someone else's
   * code, and the owner's call.
   */
  /*
   * SWITCH APPLICATION — the whole of the frontend's busy logic now. The edge
   * pushes verdicts (statusWatcher.ts); this stores them and repaints. Nothing
   * here asks anything: the failed intermediate design (edge hints → frontend
   * re-asks) is in the history for why — the re-ask raced the very window it
   * was reading through, twice, and mail delivery lit idle agents' dots.
   */
  const [, forceRender] = useState(0)
  useEffect(() => {
    const repaint = () => forceRender((n: number) => n + 1)
    switchListeners.add(repaint)
    const off = listenForSwitches()
    return () => {
      switchListeners.delete(repaint)
      off()
    }
  }, [])

  /*
   * The pty-burst ask / settle / double-confirm machinery lived here from
   * 2026-08-07 to 2026-08-30. It existed to keep a FETCHED status fresh; the
   * status now arrives as pushed switches, so the whole apparatus — and both
   * races it grew confirms for — is deleted rather than disarmed. The fetch
   * below survives only as VIEW data (kind badges, previews), refreshed on
   * user-shaped events; the dot no longer reads its status field on any
   * switch-capable edge.
   */

  /**
   * The last row we saw for each agent, for DISPLAY only.
   *
   * The aggregator asks every mounted remote for its own status and is explicitly
   * best-effort: a host that is down, slow, or mid-reconnect contributes nothing, so
   * its agents are simply absent from that tick. On a jittery link that is every few
   * seconds, and it made three separate things flicker — the view flipping to
   * terminal, the chat preview blanking, and the `Claude @ host` badge disappearing.
   *
   * NOT merged into `byKey`, and that is the whole design. `byKey` is what the poll
   * actually said, and `presenceFor` reads it: an absent row means `off`, which is
   * the truthful answer for a host nobody can reach. Synthesising rows there would
   * turn "unreachable" into "idle" — trading a visual flicker for a lie about
   * whether a machine is up.
   *
   * So this is offered separately and consumers opt in. Anything that renders an
   * identity — kind, product name, the last thing an agent said — should prefer the
   * live row and fall back here. Anything that reports LIVENESS must not.
   */
  const held = useRef(new Map<string, IdleAgent>())
  /** When each held row was last CONFIRMED, so a hold cannot outlive its evidence. */
  const heldAt = useRef(new Map<string, number>())

  return useMemo(() => {
    const byHash = new Map<string, IdleAgent>()
    const byKey = new Map<string, IdleAgent>()
    const now = Date.now()
    /** Machines that produced at least one row THIS tick — i.e. actually answered. */
    const answered = new Set<string>()
    for (const a of q.data?.agents ?? []) {
      // Keyed through the shared helper, which includes the MACHINE — see idleKey.
      const key = idleKey(a)
      byKey.set(key, a)
      if (a.hash) byHash.set(a.hash, a)
      held.current.set(key, a)
      heldAt.current.set(key, now)
      answered.add(a.device_id ?? '')
    }
    /*
     * A MACHINE WE COULD NOT ASK IS NOT AN AGENT THAT STOPPED — owner, 2026-08-10:
     * Agent 忙碌的狀態還是時不時的會消失, 很不穩定.
     *
     * The fan-out is best-effort by design, and getIdleEverywhere's own doc says
     * consumers "keep the last row they saw for its agents rather than drawing them
     * as having gone quiet". Nothing did: `presenceFor` reads `byKey`, an absent row
     * is `off`, and `off` hides the halo — so one slow relay call, one `/account/devices`
     * hiccup (which returns an EMPTY roster, not an error), and every dot blinked out
     * for a tick. Measured first: the edge itself answered `active` twelve times out
     * of twelve while the UI flickered, so the instability was never the detection.
     *
     * Carried forward ONLY when that machine contributed nothing at all this tick.
     * If it answered and this agent was simply not in the list, the absence is a real
     * answer and stays `off` — the distinction the flicker fix must not blur.
     *
     * And time-bounded: a machine that is genuinely gone stops being "recently seen"
     * after HOLD_MS, so a dead one settles to `off` instead of claiming `working`
     * for ever. That is the lie the original comment refused, and it still refuses it.
     */
    if (q.data) {
      for (const [key, row] of held.current) {
        if (byKey.has(key)) continue
        if (answered.has(row.device_id ?? '')) continue
        if (now - (heldAt.current.get(key) ?? 0) > HOLD_MS) continue
        byKey.set(key, row)
        if (row.hash) byHash.set(row.hash, row)
      }
    }
    return {
      byHash,
      byKey,
      held: held.current,
      loaded: !!q.data,
      /*
       * MACHINES THAT COULD NOT BE ASKED what their agents are doing.
       *
       * getIdleEverywhere has computed this since it was written and nothing read
       * it, so a machine whose roster answered and whose STATUS did not rendered
       * exactly like one the server had nothing to say about: agents listed, no
       * kind badge, no preview, no dot. That is the shape of the bug the owner
       * reported this morning, surviving in a narrower form after I fixed the
       * half about asking only one machine.
       *
       * Deliberately NOT the same sentence as the roster's `failed`. "Could not
       * list this machine's agents" and "listed them but could not say what they
       * are doing" call for different reactions, and merging them would undo the
       * distinction one line below the place it was won.
       */
      failed: q.data?.failed ?? [],
    }
  }, [q.data])
}

/**
 * Presence for a row: active → working; known → idle; unknown → off.
 *
 * Takes the ROW rather than loose session/host, so it cannot build a key that
 * disagrees with the one the caller used two lines earlier — which is what
 * happened while the key was a template literal typed out at each site.
 */
/**
 * Is this agent blocked on a question? Switch first, fetched row for a machine
 * whose edge has not pushed `asking` — the owner's stale answer-chip (已經回覆了
 * … 但系統還是顯示他有提問) was the fetched row outliving its truth.
 */
export function needsInputFor(
  byKey: Map<string, IdleAgent>,
  a: { device_id?: string | undefined; host?: string | null; session: string },
): boolean {
  const set = a.device_id ? askingSwitch.get(a.device_id) : undefined
  if (set) return set.has(a.session)
  return byKey.get(idleKey(a))?.needs_input === true
}

/**
 * The asking switch's three-way answer for one agent: true/false where this
 * machine's edge has pushed a verdict, null where it has not (old edge — let
 * the caller fall back to whatever lane it used before).
 */
export function askingVerdict(
  device: string | null | undefined,
  session: string,
): boolean | null {
  const set = device ? askingSwitch.get(device) : undefined
  return set ? set.has(session) : null
}

/**
 * The busy switch's three-way answer for one agent, like askingVerdict: true/false where
 * this machine's edge has pushed a verdict, null where it has not.
 */
export function busyVerdict(device: string | null | undefined, session: string): boolean | null {
  const set = device ? busySwitch.get(device) : undefined
  return set ? set.has(session) : null
}

export function presenceFor(
  byKey: Map<string, IdleAgent>,
  a: { device_id?: string | undefined; host?: string | null; session: string },
): 'working' | 'idle' | 'off' {
  const row = byKey.get(idleKey(a))
  if (!row) return 'off'
  // The edge's switches are the verdict wherever an edge has spoken; the
  // fetched row's status is only the fallback for a pre-switch edge.
  const set = a.device_id ? busySwitch.get(a.device_id) : undefined
  if (set) return set.has(a.session) ? 'working' : 'idle'
  return row.status === 'active' ? 'working' : 'idle'
}
